import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import {HeartHandshake, ArrowLeft, MapPin, Shield, Loader2, Camera, Upload, Users} from 'lucide-react'
import { ProgressBar } from '../components/ui/ProgressBar'
import { MicroText } from '../components/ui/MicroText'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { getCurrentLocation } from '../utils/geolocation'
import { LGPDConsentModal } from '../components/LGPDConsentModal'
import { analytics } from '../utils/analytics'
import { ReferralSystem } from '../components/ReferralSystem'
import { countries, brazilianStates } from '../data/countries'

const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Não sei']

export function DonorProfile() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, signIn } = useAuth()
  const [loading, setLoading] = useState(false)
  const [loadingLocation, setLoadingLocation] = useState(false)
  const [existingProfile, setExistingProfile] = useState<any>(null)
  const [showConsentModal, setShowConsentModal] = useState(false)
  const [referralBonus, setReferralBonus] = useState(0)
  const [referredByUser, setReferredByUser] = useState<any>(null)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [photoPreview, setPhotoPreview] = useState<string>('')
  
  const [formData, setFormData] = useState({
    displayName: '',
    age: '',
    bloodType: '',
    city: '',
    state: '',
    country: 'BR',
    countryCode: 'BR',
    phone: '',
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    profilePhoto: '',
    lastDonationDate: '',
    willingToDonateForAnyone: false
  })

  const calculateProgress = () => {
    let filled = 0
    if (formData.displayName) filled++
    if (formData.age) filled++
    if (formData.bloodType) filled++
    if (formData.city) filled++
    if (formData.country) filled++
    if (formData.profilePhoto) filled++
    return (filled / 6) * 100
  }

  useEffect(() => {
    if (!user) {
      toast.error('Faça login para continuar')
      signIn()
      return
    }

    const loadProfile = async () => {
      try {
        const { list } = await lumi.entities.profiles.list({
          filter: { userId: user.userId }
        })
        if (list.length > 0) {
          setExistingProfile(list[0])
          setFormData({
            displayName: list[0].displayName || '',
            age: list[0].age?.toString() || '',
            bloodType: list[0].bloodType || '',
            city: list[0].city || '',
            state: list[0].state || '',
            country: list[0].country || 'BR',
            countryCode: list[0].countryCode || 'BR',
            phone: list[0].phone || '',
            latitude: undefined,
            longitude: undefined,
            profilePhoto: list[0].profilePhoto || '',
            lastDonationDate: list[0].lastDonationDate?.split('T')[0] || '',
            willingToDonateForAnyone: list[0].willingToDonateForAnyone || false
          })
          setPhotoPreview(list[0].profilePhoto || '')
          
          // Check LGPD consent for existing profiles
          if (!list[0].lgpdConsentGiven) {
            setShowConsentModal(true)
          }
        } else {
          // New user - show consent modal
          setShowConsentModal(true)
          
          // Check for referral code
          const refCode = searchParams.get('ref')
          if (refCode) {
            await processReferral(refCode)
          }
          
          // Auto-detect location for new profiles
          detectLocation()
        }
      } catch (error) {
        console.error('Error loading profile:', error)
      }
    }
    loadProfile()
  }, [user?.userId])

  const processReferral = async (refCode: string) => {
    try {
      const { list } = await lumi.entities.profiles.list({
        filter: { referralCode: refCode }
      })
      if (list.length > 0) {
        setReferredByUser(list[0])
        setReferralBonus(50)
        toast.success(`🎁 Você ganhou +50 pontos de bônus pelo convite de ${list[0].displayName}!`, { duration: 5000 })
      }
    } catch (error) {
      console.error('Failed to process referral:', error)
    }
  }

  const detectLocation = async () => {
    setLoadingLocation(true)
    try {
      const location = await getCurrentLocation()
      if (location) {
        setFormData(prev => ({
          ...prev,
          city: location.city,
          state: location.state,
          latitude: location.latitude,
          longitude: location.longitude
        }))
        toast.success('Localização detectada automaticamente! 📍')
      }
    } catch (error) {
      console.error('Location detection failed:', error)
    } finally {
      setLoadingLocation(false)
    }
  }

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const file = files[0]
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione uma imagem válida')
      return
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB')
      return
    }

    setUploadingPhoto(true)
    try {
      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Upload to Lumi
      const results = await lumi.tools.file.upload([file])
      
      if (results[0]?.fileUrl) {
        setFormData(prev => ({ ...prev, profilePhoto: results[0].fileUrl! }))
        toast.success('Foto carregada com sucesso! 📸')
      } else if (results[0]?.uploadError) {
        toast.error(`Erro ao fazer upload: ${results[0].uploadError}`)
        setPhotoPreview('')
      }
    } catch (error) {
      console.error('Upload failed:', error)
      toast.error('Erro ao fazer upload da foto')
      setPhotoPreview('')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user) {
      toast.error('Faça login para continuar')
      return
    }

    if (!formData.displayName || !formData.age || !formData.bloodType || !formData.city || !formData.country) {
      toast.error('Preencha todos os campos obrigatórios')
      return
    }

    const age = parseInt(formData.age)
    if (age < 16 || age > 69) {
      toast.error('Idade deve estar entre 16 e 69 anos')
      return
    }

    setLoading(true)
    try {
      const profileData = {
        displayName: formData.displayName,
        age: age,
        bloodType: formData.bloodType,
        city: formData.city,
        state: formData.state || '',
        country: formData.country,
        countryCode: formData.countryCode,
        phone: formData.phone || '',
        latitude: formData.latitude,
        longitude: formData.longitude,
        userId: user.userId,
        isDonor: true,
        totalDonations: existingProfile?.totalDonations || 0,
        points: (existingProfile?.points || 0) + referralBonus,
        badges: existingProfile?.badges || [],
        referredBy: referredByUser?.userId || null,
        profilePhoto: formData.profilePhoto || '',
        lastDonationDate: formData.lastDonationDate ? new Date(formData.lastDonationDate).toISOString() : undefined,
        willingToDonateForAnyone: formData.willingToDonateForAnyone
      }

      if (existingProfile) {
        await lumi.entities.profiles.update(existingProfile._id, profileData)
        toast.success('Perfil atualizado! 🩸')
      } else {
        await lumi.entities.profiles.create(profileData)
        analytics.trackDonorRegistration(formData.bloodType, formData.city, formData.state)
        
        // Award referral points to referrer
        if (referredByUser) {
          const referrerPoints = (referredByUser.points || 0) + 75
          const referrerReferrals = (referredByUser.referrals || 0) + 1
          await lumi.entities.profiles.update(referredByUser._id, {
            points: referrerPoints,
            referrals: referrerReferrals
          })
        }
        
        toast.success('Cadastro feito! Seu nome agora pode aparecer na história de alguém que precisa de você. 🎉')
        if (referralBonus > 0) {
          setTimeout(() => {
            toast.success(`🎁 +${referralBonus} pontos de bônus creditados!`, { duration: 4000 })
          }, 1500)
        }
      }
      
      setTimeout(() => navigate('/solicitacoes'), 1500)
    } catch (error) {
      toast.error('Erro ao salvar perfil. Tente novamente.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-gray-600">Fazendo login...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {showConsentModal && user && (
        <LGPDConsentModal
          userId={user.userId}
          onConsent={() => setShowConsentModal(false)}
        />
      )}
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 max-w-lg">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        <Card className="border border-red-200 shadow-lg">
          <CardHeader className="text-center pb-4">
            {/* Profile Photo Section */}
            <div className="flex justify-center mb-4">
              <div className="relative group">
                <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-red-100 shadow-lg bg-gradient-to-br from-red-50 to-red-100">
                  {photoPreview || formData.profilePhoto ? (
                    <img 
                      src={photoPreview || formData.profilePhoto} 
                      alt="Foto de perfil" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-red-600">
                      <HeartHandshake className="h-12 w-12 text-white" />
                    </div>
                  )}
                </div>
                <label 
                  htmlFor="photoUpload" 
                  className="absolute bottom-0 right-0 w-10 h-10 bg-red-600 rounded-full flex items-center justify-center cursor-pointer shadow-lg hover:bg-red-700 transition-all group-hover:scale-110"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="h-5 w-5 text-white animate-spin" />
                  ) : (
                    <Camera className="h-5 w-5 text-white" />
                  )}
                </label>
                <input
                  id="photoUpload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">Clique na câmera para adicionar sua foto</p>
            <CardTitle className="text-3xl font-black">
              {existingProfile ? 'Atualizar Perfil' : 'Sou doador ou quero começar a doar'}
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2 font-medium">
              Se você está aqui, é porque se importa. Vamos deixar simples transformar isso em ação.
            </p>
            {!existingProfile && (
              <div className="mt-4">
                <ProgressBar progress={calculateProgress()} />
                <p className="text-xs text-gray-500 text-center mt-2">
                  {calculateProgress().toFixed(0)}% completo
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 space-y-2">
                  <p className="text-sm text-gray-700 font-medium">Como funciona:</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>✓ Você se cadastra em poucos minutos</li>
                    <li>✓ Quando alguém próximo precisa do seu tipo de sangue, você é avisado</li>
                    <li>✓ Você escolhe quando e onde quer ajudar</li>
                  </ul>
                </div>
                <div className="mt-3 text-center">
                  <p className="text-sm text-gray-700 font-medium">
                    Você entra para uma rede de pessoas que não esperam o problema bater na porta para agir.
                  </p>
                </div>
                <div className="flex justify-center gap-4 mt-3">
                  <MicroText variant="success" icon={Shield}>Gratuito para sempre</MicroText>
                  <MicroText variant="info">Sem spam</MicroText>
                </div>
              </div>
            )}
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Info sobre rede social */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                <Users className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-bold text-gray-900 mb-1">✨ Agora somos uma rede social!</p>
                  <p className="text-xs text-gray-700">
                    Crie seu perfil completo, compartilhe sua história e conecte-se com outros doadores. Juntos salvamos mais vidas!
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="displayName" className="block text-sm font-bold text-gray-700 mb-2">
                  Nome Completo <span className="text-red-600">*</span>
                </label>
                <Input
                  id="displayName"
                  type="text"
                  placeholder="Como você se chama?"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  required
                  aria-required="true"
                  className="h-12 text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Idade <span className="text-red-600">*</span>
                </label>
                <Input
                  type="number"
                  placeholder="Sua idade"
                  min="16"
                  max="69"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  required
                  className="h-12 text-base"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Doadores devem ter entre 16 e 69 anos
                </p>
              </div>

              <div>
                <label htmlFor="bloodType" className="block text-sm font-bold text-gray-700 mb-2">
                  Tipo Sanguíneo <span className="text-red-600">*</span>
                </label>
                <Select
                  id="bloodType"
                  value={formData.bloodType}
                  onChange={(e) => setFormData({ ...formData, bloodType: e.target.value })}
                  required
                  aria-required="true"
                  className="h-12 text-base"
                >
                  <option value="">Selecione</option>
                  {bloodTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Não sabe? Selecione "Não sei" e descubra ao doar
                </p>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  País <span className="text-red-600">*</span>
                </label>
                <Select
                  value={formData.country}
                  onChange={(e) => {
                    const selectedCountry = countries.find(c => c.name === e.target.value)
                    setFormData({ 
                      ...formData, 
                      country: e.target.value,
                      countryCode: selectedCountry?.code || '',
                      state: selectedCountry?.code === 'BR' ? formData.state : ''
                    })
                  }}
                  required
                  className="h-12 text-base"
                >
                  <option value="">Selecione seu país</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.name}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  🌍 Agora aceitamos doadores de todo o mundo!
                </p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-bold text-gray-700">
                    Localização <span className="text-red-600">*</span>
                  </label>
                  {!existingProfile && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={detectLocation}
                      disabled={loadingLocation}
                      className="text-xs"
                    >
                      {loadingLocation ? (
                        <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Detectando...</>
                      ) : (
                        <><MapPin className="h-3 w-3 mr-1" /> Detectar automaticamente</>
                      )}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    type="text"
                    placeholder="Sua cidade"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    required
                    className="h-12 text-base"
                  />
                  {formData.countryCode === 'BR' ? (
                    <Select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="h-12 text-base"
                    >
                      <option value="">UF</option>
                      {brazilianStates.map(state => (
                        <option key={state.code} value={state.code}>{state.code}</option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      type="text"
                      placeholder="Estado/Região"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="h-12 text-base"
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  WhatsApp (opcional)
                </label>
                <Input
                  type="tel"
                  placeholder="(00) 00000-0000"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12 text-base"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Para contato direto quando houver match
                </p>
              </div>

              {/* Last Donation Date */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Última Doação (opcional)
                </label>
                <Input
                  type="date"
                  value={formData.lastDonationDate}
                  onChange={(e) => setFormData({ ...formData, lastDonationDate: e.target.value })}
                  max={new Date().toISOString().split('T')[0]}
                  className="h-12 text-base"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Quando foi sua última doação de sangue? Isso nos ajuda a saber quando você pode doar novamente.
                </p>
              </div>

              {/* Willing to Donate for Anyone */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={formData.willingToDonateForAnyone}
                    onChange={(e) => setFormData({ ...formData, willingToDonateForAnyone: e.target.checked })}
                    className="mt-1 h-5 w-5 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-gray-900 group-hover:text-green-700 transition-colors">
                      💚 Quero doar mesmo que não seja do meu tipo sanguíneo
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Ative esta opção para aparecer em todas as solicitações, independente da compatibilidade. 
                      Você pode doar "em nome de" alguém ou ajudar de outras formas. Quanto mais doadores, mais vidas salvamos!
                    </p>
                  </div>
                </label>
              </div>

              <div className="bg-white border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-bold text-gray-900 mb-1">Sistema de Match Inteligente</p>
                  <p className="text-xs">
                    Usamos sua localização para conectar você automaticamente com pedidos próximos do seu tipo sanguíneo
                  </p>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full h-14 text-lg font-bold bg-red-600 hover:bg-red-700 text-white shadow-md"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Salvando perfil...
                  </span>
                ) : existingProfile ? 'Atualizar Perfil' : '🩸 Cadastrar meu perfil de doador'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {existingProfile && (
          <div className="mt-8">
            <ReferralSystem />
          </div>
        )}
      </div>
      <Footer />
    </div>
  )
}
